import { useEffect, useState } from "react";
import { IS_STATIC_BUILD } from "../lib/api";
import { apiMonitorAdd, apiMonitorStatus, ContaError } from "../lib/conta";
import { usePlatform, useSession } from "../lib/session";
import { navigate } from "../lib/useHashRoute";
import { IconBell, IconCheck } from "./Icons";

type State =
  | { kind: "idle" }
  | { kind: "checking" }
  | { kind: "busy" }
  | { kind: "monitored" }
  | { kind: "limit"; message: string }
  | { kind: "error"; message: string };

/**
 * "Monitorar" na ficha. Visitante vai para o login e volta para a mesma ficha;
 * quem tem conta passa a acompanhar a empresa ali mesmo, sem sair da página.
 */
export function MonitorButton({ cnpj }: { cnpj: string }) {
  const session = useSession();
  const platform = usePlatform();
  const [state, setState] = useState<State>({ kind: "idle" });
  const logged = session.status === "auth";

  useEffect(() => {
    if (!logged) return;
    const controller = new AbortController();
    setState({ kind: "checking" });
    apiMonitorStatus(cnpj, controller.signal)
      .then((result) => setState(result.monitorado ? { kind: "monitored" } : { kind: "idle" }))
      .catch(() => setState({ kind: "idle" }));
    return () => controller.abort();
  }, [cnpj, logged]);

  if (IS_STATIC_BUILD || platform?.contas === false || session.status === "loading") return null;

  if (!logged) {
    return (
      <button
        className="btn btn-secondary"
        onClick={() => navigate({ page: "login", volta: `/empresa/${cnpj}` })}
        title="Entre para acompanhar as mudanças desta empresa"
      >
        <IconBell size={15} /> Monitorar
      </button>
    );
  }

  if (state.kind === "monitored") {
    return (
      <button
        className="btn btn-secondary monitor-on"
        onClick={() => navigate({ page: "account", tab: "monitoramento" })}
        title="Ver as empresas monitoradas e o histórico de mudanças"
      >
        <IconCheck size={15} /> Monitorando
      </button>
    );
  }

  const add = async () => {
    setState({ kind: "busy" });
    try {
      await apiMonitorAdd(cnpj);
      setState({ kind: "monitored" });
    } catch (error) {
      if (error instanceof ContaError && error.code === "limite_monitoramento") {
        setState({ kind: "limit", message: error.message });
      } else if (error instanceof ContaError && error.status === 401) {
        navigate({ page: "login", volta: `/empresa/${cnpj}` });
      } else {
        setState({ kind: "error", message: error instanceof Error ? error.message : "Não foi possível agora." });
      }
    }
  };

  return (
    <span className="monitor-wrap">
      <button className="btn btn-secondary" onClick={add} disabled={state.kind === "busy" || state.kind === "checking"}>
        {state.kind === "busy" ? <span className="spinner" aria-hidden /> : <IconBell size={15} />} Monitorar
      </button>
      {(state.kind === "limit" || state.kind === "error") && (
        <span className="monitor-note" role="alert">
          {state.message}
          {state.kind === "limit" && (
            <button className="link-btn" onClick={() => navigate({ page: "account", tab: "plano" })}>
              Ver planos
            </button>
          )}
        </span>
      )}
    </span>
  );
}
